import type { Locale } from '../types'

/**
 * Entry flow dictionary: splash, language, authentication and onboarding.
 * Merged into the same `t()` lookup by LocaleContext, so the product still has
 * ONE translation entry point.
 */
export const entryStrings: Record<Locale, Record<string, string>> = {
  'pt-BR': {
    /* Splash */
    'entry.splash.tag': 'Corpo, mente e hábitos',
    'entry.splash.loading': 'Preparando seu dia',

    /* Language */
    'entry.lang.title': 'Escolha seu idioma',
    'entry.lang.body':
      'Você pode mudar depois em Configurações. Todo o produto acompanha essa escolha.',
    'entry.lang.continue': 'Continuar',
    'entry.lang.ptBR': 'Português (Brasil)',
    'entry.lang.ptBR.hint': 'Idioma padrão do BEELYV',
    'entry.lang.enUS': 'English (US)',
    'entry.lang.enUS.hint': 'Full product translation',

    /* Login */
    'entry.login.title': 'Bem-vindo de volta',
    'entry.login.body': 'Entre para continuar sua jornada.',
    'entry.login.email': 'E-mail',
    'entry.login.password': 'Senha',
    'entry.login.submit': 'Entrar',
    'entry.login.forgot': 'Esqueci minha senha',
    'entry.login.noAccount': 'Ainda não tem conta?',
    'entry.login.create': 'Criar conta',
    'entry.login.or': 'ou continue com',
    'entry.login.apple': 'Continuar com Apple',
    'entry.login.google': 'Continuar com Google',
    'entry.login.invalid': 'Verifique o e-mail e a senha para continuar.',

    /* Create account */
    'entry.create.title': 'Criar sua conta',
    'entry.create.body': 'Leva menos de um minuto.',
    'entry.create.name': 'Nome',
    'entry.create.username': 'Nome de usuário',
    'entry.create.username.hint': 'Aparece no seu perfil público.',
    'entry.create.email': 'E-mail',
    'entry.create.password': 'Senha',
    'entry.create.password.hint': 'Pelo menos 8 caracteres.',
    'entry.create.confirm': 'Confirmar senha',
    'entry.create.mismatch': 'As senhas não coincidem.',
    'entry.create.consent':
      'Li e aceito os Termos de Uso e a Política de Privacidade, incluindo o tratamento dos dados de saúde que eu escolher registrar.',
    'entry.create.consentNote':
      'Suas métricas corporais são privadas por padrão e só ficam públicas com seu consentimento explícito.',
    'entry.create.submit': 'Criar conta',
    'entry.create.hasAccount': 'Já tem conta?',
    'entry.create.login': 'Entrar',

    /* Forgot password */
    'entry.forgot.title': 'Recuperar acesso',
    'entry.forgot.body':
      'Informe o e-mail da sua conta e enviaremos um link para redefinir sua senha.',
    'entry.forgot.email': 'E-mail',
    'entry.forgot.submit': 'Enviar link de redefinição',
    'entry.forgot.sent.tag': 'E-mail enviado',
    'entry.forgot.sent.title': 'Verifique seu e-mail',
    'entry.forgot.sent.body':
      'Se existir uma conta com esse e-mail, o link de redefinição chega em alguns minutos.',
    'entry.forgot.back': 'Voltar ao login',

    /* Account confirmation */
    'entry.confirm.tag': 'Confirmação de conta',
    'entry.confirm.title': 'Confirme seu e-mail',
    'entry.confirm.body':
      'Enviamos um link de confirmação para o e-mail cadastrado. Confirmar garante a recuperação da sua conta.',
    'entry.confirm.p1': 'O link expira em 24 horas',
    'entry.confirm.p2': 'Você pode continuar e confirmar depois',
    'entry.confirm.continue': 'Continuar',
    'entry.confirm.resend': 'Reenviar e-mail',

    /* Onboarding chrome */
    'onb.step': 'Etapa {current} de {total}',
    'onb.continue': 'Continuar',
    'onb.back': 'Voltar',
    'onb.skip': 'Pular por agora',

    /* Intro */
    'onb.intro.tag': 'Bem-vindo ao BEELYV',
    'onb.intro.title': 'Cinco frentes, um só painel.',
    'onb.intro.body':
      'Você registra seu dia em poucos toques. O BEELYV transforma isso em progresso visível.',
    'onb.intro.body1': 'Corpo',
    'onb.intro.body1.hint': 'Composição, peso e evolução no seu ritmo',
    'onb.intro.mind': 'Mente',
    'onb.intro.mind.hint': 'Humor, clareza e descanso registrados sem julgamento',
    'onb.intro.habits': 'Hábitos',
    'onb.intro.habits.hint': 'Daily Check de 100 XP, sequência e disciplina construída',
    'onb.intro.community': 'Comunidade',
    'onb.intro.community.hint': 'Sempre depois do seu cuidado, nunca antes',
    'onb.intro.performance': 'Performance',
    'onb.intro.performance.hint': 'Corrida, ciclismo, natação, CrossFit e musculação',
    'onb.intro.start': 'Vamos personalizar',

    /* Goals */
    'onb.goals.title': 'O que você quer alcançar?',
    'onb.goals.body': 'Escolha quantos quiser. Isso organiza sua Home e seus insights.',
    'onb.goal.health': 'Melhorar saúde',
    'onb.goal.habits': 'Criar hábitos',
    'onb.goal.fitness': 'Melhorar condicionamento',
    'onb.goal.performance': 'Performance esportiva',
    'onb.goal.mental': 'Bem-estar mental',
    'onb.goal.sleep': 'Dormir melhor',
    'onb.goal.balance': 'Equilíbrio da rotina',

    /* Activities */
    'onb.act.title': 'O que você pratica?',
    'onb.act.body': 'Define quais métricas de performance aparecem no seu perfil.',
    'onb.act.strength': 'Musculação',
    'onb.act.running': 'Corrida',
    'onb.act.cycling': 'Ciclismo',
    'onb.act.swimming': 'Natação',
    'onb.act.triathlon': 'Triatlo',
    'onb.act.crossfit': 'CrossFit',
    'onb.act.mobility': 'Mobilidade',
    'onb.act.mindfulness': 'Mindfulness',
    'onb.act.nutrition': 'Nutrição',

    /* Preferences */
    'onb.pref.title': 'Suas preferências',
    'onb.pref.body': 'Vale para todas as métricas, gráficos e conversões do produto.',
    'onb.pref.units': 'Sistema de medida',
    'onb.pref.metric': 'Métrico',
    'onb.pref.metric.hint': 'kg · km · cm · min/km · /100m',
    'onb.pref.imperial': 'Imperial',
    'onb.pref.imperial.hint': 'lbs · mi · in · min/mi · /100yd',
    'onb.pref.language': 'Idioma',

    /* Permissions */
    'onb.perm.title': 'Antes de pedir acesso',
    'onb.perm.body':
      'Explicamos para que serve cada permissão. Você pode recusar agora e liberar depois em Configurações.',
    'onb.perm.notifications': 'Notificações',
    'onb.perm.notifications.hint':
      'Lembretes do Daily Check e a Revisão Semanal. Avisos de saúde vêm sempre antes dos sociais.',
    'onb.perm.camera': 'Câmera e fotos',
    'onb.perm.camera.hint':
      'Necessária para a Foto do Dia e para publicar Pulse e Post.',
    'onb.perm.location': 'Localização',
    'onb.perm.location.hint':
      'Opcional. Só é usada quando você escolhe marcar o local de uma atividade.',
    'onb.perm.allow': 'Permitir',
    'onb.perm.later': 'Agora não',
    'onb.perm.granted': 'Permitido',
    'onb.perm.denied': 'Recusado',
    'onb.perm.deniedNote':
      'Sem essa permissão, a parte correspondente fica indisponível e o app explica o motivo quando você tentar usá-la.',
    'onb.perm.finish': 'Concluir',

    /* Complete */
    'onb.done.tag': 'Tudo pronto',
    'onb.done.title': 'Seu painel está montado.',
    'onb.done.body':
      'Seu primeiro Daily Check vale 100 XP. Comece por onde fizer sentido hoje.',
    'onb.done.p1': 'Life Score no topo da sua Home',
    'onb.done.p2': 'Post liberado ao alcançar 50 XP no dia',
    'onb.done.p3': 'Comunidade sempre depois do seu progresso',
    'onb.done.cta': 'Começar minha jornada',
  },

  'en-US': {
    'entry.splash.tag': 'Body, mind and habits',
    'entry.splash.loading': 'Preparing your day',

    'entry.lang.title': 'Choose your language',
    'entry.lang.body':
      'You can change this later in Settings. The whole product follows this choice.',
    'entry.lang.continue': 'Continue',
    'entry.lang.ptBR': 'Português (Brasil)',
    'entry.lang.ptBR.hint': 'Beelyv default language',
    'entry.lang.enUS': 'English (US)',
    'entry.lang.enUS.hint': 'Full product translation',

    'entry.login.title': 'Welcome back',
    'entry.login.body': 'Sign in to continue your journey.',
    'entry.login.email': 'Email',
    'entry.login.password': 'Password',
    'entry.login.submit': 'Sign in',
    'entry.login.forgot': 'I forgot my password',
    'entry.login.noAccount': 'No account yet?',
    'entry.login.create': 'Create account',
    'entry.login.or': 'or continue with',
    'entry.login.apple': 'Continue with Apple',
    'entry.login.google': 'Continue with Google',
    'entry.login.invalid': 'Check the email and password to continue.',

    'entry.create.title': 'Create your account',
    'entry.create.body': 'It takes less than a minute.',
    'entry.create.name': 'Name',
    'entry.create.username': 'Username',
    'entry.create.username.hint': 'Shown on your public profile.',
    'entry.create.email': 'Email',
    'entry.create.password': 'Password',
    'entry.create.password.hint': 'At least 8 characters.',
    'entry.create.confirm': 'Confirm password',
    'entry.create.mismatch': 'The passwords do not match.',
    'entry.create.consent':
      'I have read and accept the Terms of Use and the Privacy Policy, including the handling of the health data I choose to record.',
    'entry.create.consentNote':
      'Your body metrics are private by default and only become public with your explicit consent.',
    'entry.create.submit': 'Create account',
    'entry.create.hasAccount': 'Already have an account?',
    'entry.create.login': 'Sign in',

    'entry.forgot.title': 'Recover access',
    'entry.forgot.body':
      'Enter your account email and we will send a link to reset your password.',
    'entry.forgot.email': 'Email',
    'entry.forgot.submit': 'Send reset link',
    'entry.forgot.sent.tag': 'Email sent',
    'entry.forgot.sent.title': 'Check your email',
    'entry.forgot.sent.body':
      'If an account exists for that email, the reset link arrives in a few minutes.',
    'entry.forgot.back': 'Back to sign in',

    'entry.confirm.tag': 'Account confirmation',
    'entry.confirm.title': 'Confirm your email',
    'entry.confirm.body':
      'We sent a confirmation link to your email. Confirming keeps account recovery available.',
    'entry.confirm.p1': 'The link expires in 24 hours',
    'entry.confirm.p2': 'You can continue and confirm later',
    'entry.confirm.continue': 'Continue',
    'entry.confirm.resend': 'Resend email',

    'onb.step': 'Step {current} of {total}',
    'onb.continue': 'Continue',
    'onb.back': 'Back',
    'onb.skip': 'Skip for now',

    'onb.intro.tag': 'Welcome to Beelyv',
    'onb.intro.title': 'Five fronts, one dashboard.',
    'onb.intro.body':
      'You log your day in a few taps. Beelyv turns that into visible progress.',
    'onb.intro.body1': 'Body',
    'onb.intro.body1.hint': 'Composition, weight and progress at your own pace',
    'onb.intro.mind': 'Mind',
    'onb.intro.mind.hint': 'Mood, clarity and rest logged without judgement',
    'onb.intro.habits': 'Habits',
    'onb.intro.habits.hint': '100 XP Daily Check, streak and discipline built',
    'onb.intro.community': 'Community',
    'onb.intro.community.hint': 'Always after your own care, never before it',
    'onb.intro.performance': 'Performance',
    'onb.intro.performance.hint': 'Running, cycling, swimming, CrossFit and strength',
    'onb.intro.start': "Let's personalize",

    'onb.goals.title': 'What do you want to achieve?',
    'onb.goals.body': 'Pick as many as you like. This shapes your Home and insights.',
    'onb.goal.health': 'Improve health',
    'onb.goal.habits': 'Build habits',
    'onb.goal.fitness': 'Improve fitness',
    'onb.goal.performance': 'Sports performance',
    'onb.goal.mental': 'Mental wellbeing',
    'onb.goal.sleep': 'Sleep better',
    'onb.goal.balance': 'Routine balance',

    'onb.act.title': 'What do you practice?',
    'onb.act.body': 'This decides which performance metrics appear on your profile.',
    'onb.act.strength': 'Strength training',
    'onb.act.running': 'Running',
    'onb.act.cycling': 'Cycling',
    'onb.act.swimming': 'Swimming',
    'onb.act.triathlon': 'Triathlon',
    'onb.act.crossfit': 'CrossFit',
    'onb.act.mobility': 'Mobility',
    'onb.act.mindfulness': 'Mindfulness',
    'onb.act.nutrition': 'Nutrition',

    'onb.pref.title': 'Your preferences',
    'onb.pref.body': 'Applies to every metric, chart and conversion in the product.',
    'onb.pref.units': 'Measurement system',
    'onb.pref.metric': 'Metric',
    'onb.pref.metric.hint': 'kg · km · cm · min/km · /100m',
    'onb.pref.imperial': 'Imperial',
    'onb.pref.imperial.hint': 'lbs · mi · in · min/mi · /100yd',
    'onb.pref.language': 'Language',

    'onb.perm.title': 'Before we ask for access',
    'onb.perm.body':
      'Here is what each permission is for. You can decline now and allow it later in Settings.',
    'onb.perm.notifications': 'Notifications',
    'onb.perm.notifications.hint':
      'Daily Check reminders and the Weekly Review. Health alerts always come before social ones.',
    'onb.perm.camera': 'Camera and photos',
    'onb.perm.camera.hint': 'Needed for the Photo of the Day and to publish Pulse and Post.',
    'onb.perm.location': 'Location',
    'onb.perm.location.hint':
      'Optional. Only used when you choose to tag the place of an activity.',
    'onb.perm.allow': 'Allow',
    'onb.perm.later': 'Not now',
    'onb.perm.granted': 'Allowed',
    'onb.perm.denied': 'Declined',
    'onb.perm.deniedNote':
      'Without this permission that part stays unavailable, and the app explains why when you try to use it.',
    'onb.perm.finish': 'Finish',

    'onb.done.tag': 'All set',
    'onb.done.title': 'Your dashboard is ready.',
    'onb.done.body':
      'Your first Daily Check is worth 100 XP. Start wherever makes sense today.',
    'onb.done.p1': 'Life Score at the top of your Home',
    'onb.done.p2': 'Post unlocks when you reach 50 XP today',
    'onb.done.p3': 'Community always after your own progress',
    'onb.done.cta': 'Start my journey',
  },
}
